import { hookstate, useHookstate } from "@hookstate/core";

/**
 * Tracks the post-Stripe-return polling window so UI surfaces can show a
 * "Confirming your purchase…" state instead of rendering stale "buy this"
 * CTAs while the SPA waits for the fulfillment webhook to land. Cleared when
 * polling resolves (success or timeout) so a refresh can retry without the
 * banner being permanently stuck.
 */
type SubscriptionKey = "subscription";
const SUBSCRIPTION_KEY: SubscriptionKey = "subscription";

type StripePaymentSettlingState = {
  settlingKeys: Record<string, true>;
};

export const stripePaymentSettlingStore = hookstate<StripePaymentSettlingState>({
  settlingKeys: {},
});

function buildEntityKey(
  commerceKind: string,
  entityType: string,
  entityId: number,
): string {
  return `${commerceKind}:${entityType}:${entityId}`;
}

export function markEntityStripePaymentSettling(
  commerceKind: string,
  entityType: string,
  entityId: number,
): void {
  if (
    !commerceKind ||
    !entityType ||
    !Number.isFinite(entityId) ||
    entityId <= 0
  ) {
    return;
  }
  const key = buildEntityKey(commerceKind, entityType, entityId);
  stripePaymentSettlingStore.settlingKeys.merge({ [key]: true });
}

export function clearEntityStripePaymentSettling(
  commerceKind: string,
  entityType: string,
  entityId: number,
): void {
  if (
    !commerceKind ||
    !entityType ||
    !Number.isFinite(entityId) ||
    entityId <= 0
  ) {
    return;
  }
  const key = buildEntityKey(commerceKind, entityType, entityId);
  const current = {
    ...stripePaymentSettlingStore.settlingKeys.get({ noproxy: true }),
  };
  if (!(key in current)) return;
  delete current[key];
  stripePaymentSettlingStore.settlingKeys.set(current);
}

export function markSubscriptionStripePaymentSettling(): void {
  stripePaymentSettlingStore.settlingKeys.merge({ [SUBSCRIPTION_KEY]: true });
}

export function clearSubscriptionStripePaymentSettling(): void {
  const current = {
    ...stripePaymentSettlingStore.settlingKeys.get({ noproxy: true }),
  };
  if (!(SUBSCRIPTION_KEY in current)) return;
  delete current[SUBSCRIPTION_KEY];
  stripePaymentSettlingStore.settlingKeys.set(current);
}

export function clearAllStripePaymentSettling(): void {
  stripePaymentSettlingStore.settlingKeys.set({});
}

export function useIsEntityStripePaymentSettling(
  commerceKind: string | undefined,
  entityType: string | undefined,
  entityId: number | undefined,
): boolean {
  const state = useHookstate(stripePaymentSettlingStore.settlingKeys);
  if (
    !commerceKind ||
    !entityType ||
    typeof entityId !== "number" ||
    !Number.isFinite(entityId) ||
    entityId <= 0
  ) {
    return false;
  }
  return state.value[buildEntityKey(commerceKind, entityType, entityId)] === true;
}

export function useIsSubscriptionStripePaymentSettling(): boolean {
  const state = useHookstate(stripePaymentSettlingStore.settlingKeys);
  return state.value[SUBSCRIPTION_KEY] === true;
}

export function useIsAnyStripePaymentSettling(): boolean {
  const state = useHookstate(stripePaymentSettlingStore.settlingKeys);
  return Object.keys(state.value).length > 0;
}
